import { SMB_PLATFORM } from "@/runtime-env";
import type { NavigateFunction, NavigateOptions } from "react-router-dom";

/**
 * A self-service deployment serves two apps from one bundle: the business app at the root and
 * the dashboard, as the operators' panel, under /admin. Each page load starts one of them with
 * its own router. The panel's router has a basename, so the dashboard's links stay exactly what
 * they are on an enterprise deployment, where it is the whole app at the root. Crossing from one
 * app to the other is therefore a page load, never a router navigation.
 */
export const ADMIN_BASE = "/admin";

/** True when this page load is the operators' panel of a self-service deployment. */
export const IN_ADMIN = SMB_PLATFORM && /^\/admin(\/|$)/.test(globalThis.location.pathname);

/** True when this page load is the business app, the only place a business can sign itself up. */
export const IN_SMB = SMB_PLATFORM && !IN_ADMIN;

/** The address as this app's router sees it, for code that reads the browser's own. */
export function appPathname(): string {
	const { pathname } = globalThis.location;
	return IN_ADMIN ? pathname.slice(ADMIN_BASE.length) || "/" : pathname;
}

/** A path of this app as the browser needs it, for links that bypass the router. */
export function appHref(path: string): string {
	return IN_ADMIN ? ADMIN_BASE + path : path;
}

/** Goes to a page of the business app or of the dashboard, whichever app is running now. */
export function goTo(navigate: NavigateFunction, app: "smb" | "admin", path: string, options?: NavigateOptions): void {
	if (!SMB_PLATFORM || (app === "admin") === IN_ADMIN) {
		void navigate(path, options);
		return;
	}
	globalThis.location.replace(app === "admin" ? ADMIN_BASE + path : path);
}
